import React, { useMemo, useRef } from "react";
import { type CustomReportResults } from "shared/api/types/pagination";
import { type CustomReportResponseType } from "shared/api/types/society/[societyId]/reports/[reportId]";
import PillarTableButtonCellContent from "shared/components/pillar-table/cell-content/PillarTableButtonCellContent";
import PillarTableColumn from "shared/components/pillar-table/columns/PillarTableColumn";
import PillarTableCheckboxColumn from "shared/components/pillar-table/columns/PillarTableColumnCheckbox";
import PillarTableCreateModifiedColumn from "shared/components/pillar-table/columns/PillarTableColumnCreateModified";
import PillarTableDateTimeColumn from "shared/components/pillar-table/columns/PillarTableColumnDateTime";
import PillarTableTemplateColumn from "shared/components/pillar-table/columns/PillarTableColumnTemplate";
import PillarTableUSDColumn from "shared/components/pillar-table/columns/PillarTableColumnUSD";
import { usePillarTableColumnsValues } from "shared/components/pillar-table/columns/usePillarTableColumnsValues";
import { renderCustomReportColumn as renderCustomReportColumnFn } from "shared/components/pillar-table/custom-reports/customReportColumnsMapper";
import { AppExportToExcelBook } from "shared/components/pillar-table/export/advanced/AppExportToExcelBook";
import PillarTableExportToExcelDynamicColumn from "shared/components/pillar-table/export/advanced/AppExportToExcelDynamicColumn";
import { AppExportToExcelSheet } from "shared/components/pillar-table/export/advanced/AppExportToExcelSheet";
import { AppMultipleExportToExcel } from "shared/components/pillar-table/export/advanced/AppMultipleExportToExcel";
import PillarTableExportToExcel from "shared/components/pillar-table/export/PillarTableExportToExcel";
import PillarTableExportToExcelColumn from "shared/components/pillar-table/export/PillarTableExportToExcelColumn";
import PillarTableFilterCheckbox from "shared/components/pillar-table/filters/PillarTableFilterCheckbox";
import PillarTableFilterDatePicker from "shared/components/pillar-table/filters/PillarTableFilterDatePicker";
import PillarTableFilterHidden from "shared/components/pillar-table/filters/PillarTableFilterHidden";
import PillarTableFilters from "shared/components/pillar-table/filters/PillarTableFilters";
import PillarTableFilterSelectMenu from "shared/components/pillar-table/filters/PillarTableFilterSelectMenu";
import PillarTableFilterSingleDate from "shared/components/pillar-table/filters/PillarTableFilterSingleDate";
import PillarTableFilterTextInput from "shared/components/pillar-table/filters/PillarTableFilterTextInput";
import usePillarTableFiltersValues from "shared/components/pillar-table/filters/usePillarTableFiltersValues";
import PillarTableHeaderBar from "shared/components/pillar-table/header/PillarTableHeaderBar";
import { usePillarTableQueryValues } from "shared/components/pillar-table/hooks/usePillarTableQueryValues";
import { usePillarTableVirtualValues } from "shared/components/pillar-table/hooks/usePillarTableVirtualValues";
import PillarTableNestedTable from "shared/components/pillar-table/nested/PillarTableNestedTable";
import usePillarTablePaginationSorting from "shared/components/pillar-table/pagination-sorting/usePillarTablePaginationSorting";
import {
  PillarTableContext,
  type PillarTableContextFactoryProps,
  type DataQueryFunction as NewLocationNamedDataQueryFunction,
} from "shared/components/pillar-table/PillarTableContext";
import PillarTableMain from "shared/components/pillar-table/PillarTableMain";
import PillarTablePopoverMenu from "shared/components/pillar-table/PillarTablePopoverMenu";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { createComponentWrapper } from "shared/components/pillar-table/wrappers/PillarTableComponentWrapper";

/**
 * @deprecated Import from "shared/components/pillar-table/PillarTableContext" instead.
 */
export type QueryTableData<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
> = NewLocationNamedDataQueryFunction<RowGeneric, ExtraPropsGeneric>;

const PillarTable = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>({
  pillarTableContext,
  pagination: paginationEnabled = true,
  renderCustomReportColumn,
  isTableWithFixedHeight: externalIsTableWithFixedHeight,
  ...props
}: Partial<
  PillarTableContextFactoryProps<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >
>) => {
  const {
    FilterComponentWrapper,
    filtersRequest,
    filtersRendered,
    setFilters,
  } = usePillarTableFiltersValues({
    children: pillarTableContext?.children ?? props.children,
    urlParams: props.urlParams,
  });

  const HeaderBarComponentWrapper = useMemo(
    () =>
      pillarTableContext?.HeaderBarComponentWrapper ??
      createComponentWrapper(
        PillarTableHeaderBar,
        pillarTableContext?.children ?? props.children,
      ),
    [pillarTableContext, props.children],
  );

  const { paginationState, setPaginationState, toggleSorting } =
    usePillarTablePaginationSorting<RowGeneric>({
      filtersRequest,
      pagination: !!paginationEnabled,
      urlParams: false,
    });

  const tablePaginationRef = useRef<HTMLDivElement>(null);

  const {
    results,
    latestPageData,
    dataQueryError,
    isLoading,
    isFetching,
    isFetched,
    isRefetching,
    refetch: queryResultRefetch,
    hasPreviousPage,
    hasNextPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    fetchPreviousPage,
    fetchNextPage,
  } = usePillarTableQueryValues<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >({
    queryKey: props.queryKey,
    dataQuery: props.dataQuery,
    filtersRequest,
    filtersRendered,
    setFilters,
    pagination: paginationEnabled ?? false,
    paginationState,
    setPaginationState,
  });

  const customReportResponse = latestPageData as
    | CustomReportResults<CustomReportResponseType>
    | undefined;

  const { fixedColumnWidths, columnRefs, columns } =
    usePillarTableColumnsValues({
      children: pillarTableContext?.children ?? props.children,
      displayColumns: (
        pillarTableContext?.customReportResponse ?? customReportResponse
      )?.displayColumns,
      pagination: paginationEnabled ?? false,
    });

  const {
    tableRef,
    virtualRows,
    totalTableHeight,
    isInfinityTable,
    isTableWithFixedHeight,
  } = usePillarTableVirtualValues<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >({
    pagination: paginationEnabled ?? false,
    tableRowResults: pillarTableContext?.tableRowResults || results || [],
    isTableWithFixedHeight: externalIsTableWithFixedHeight,
    isLoading,
    isFetching,
    isRefetching,
    hasPreviousPage,
    hasNextPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    fetchPreviousPage,
    fetchNextPage,
  });

  return (
    <PillarTableContext.Provider
      value={{
        emptyTableMessage: props.emptyTableMessage,
        loadedFirstTime: isFetched,
        customReportResponse: customReportResponse,
        tableRowResults: results || [],
        dataQuery: props.dataQuery,
        dataQueryError,
        dynamicFilterChoices: latestPageData?.dynamicFilterChoices || {},
        totalResults: latestPageData?.totalResults || 0,
        extraProps: latestPageData?.extraProps,
        columns,
        filtersRequest,
        setFilters,
        refetchData: queryResultRefetch,
        HeaderBarComponentWrapper,
        FilterComponentWrapper,
        className: props.className,
        testidTable: getTableTestId({ pillarTableContext, ...props }),
        pagination: paginationEnabled ?? false,
        tablePaginationRef,
        paginationState,
        setPaginationState,
        toggleSorting,
        fixedColumnWidths,
        handleRowClick: props.handleRowClick,
        handleInlineEdit: props.handleInlineEdit,
        handleInlineDelete: props.handleInlineDelete,
        columnRefs,
        isFetchingPreviousPage,
        isFetchingNextPage,
        tableRef,
        virtualRows,
        totalTableHeight,
        isInfinityTable,
        isTableWithFixedHeight,
        renderCustomReportColumn:
          renderCustomReportColumn ?? renderCustomReportColumnFn,
        ...props,
        ...pillarTableContext,
      }}
    >
      <PillarTableMain />
    </PillarTableContext.Provider>
  );
};

PillarTable.Column = PillarTableColumn;
PillarTable.CheckboxColumn = PillarTableCheckboxColumn;
PillarTable.TemplateColumn = PillarTableTemplateColumn;
PillarTable.DateTimeColumn = PillarTableDateTimeColumn;
PillarTable.USDColumn = PillarTableUSDColumn;
PillarTable.CreateModifiedColumn = PillarTableCreateModifiedColumn;

PillarTable.Button = PillarTableButtonCellContent;

PillarTable.PopoverMenu = PillarTablePopoverMenu;
PillarTable.NestedTable = PillarTableNestedTable;
PillarTable.HeaderBar = PillarTableHeaderBar;
PillarTable.Filters = PillarTableFilters;
PillarTable.FilterCheckbox = PillarTableFilterCheckbox;
PillarTable.FilterDatePicker = PillarTableFilterDatePicker;
PillarTable.FilterHidden = PillarTableFilterHidden;
PillarTable.FilterSelectMenu = PillarTableFilterSelectMenu;
PillarTable.FilterSingleDate = PillarTableFilterSingleDate;
PillarTable.FilterTextInput = PillarTableFilterTextInput;

/**
 * @deprecated Each Export Column should just be defined as a regular Column.
 */
PillarTable.ExportToExcelColumn = PillarTableExportToExcelColumn;
PillarTable.ExportToExcel = PillarTableExportToExcel;
PillarTable.MultipleExportToExcel = AppMultipleExportToExcel;
PillarTable.ExportToExcelSheet = AppExportToExcelSheet;
PillarTable.ExportToExcelDynamicColumn = PillarTableExportToExcelDynamicColumn;
PillarTable.ExportToExcelBook = AppExportToExcelBook;

export default PillarTable as typeof PillarTable &
  ((props: Partial<PillarTableContextFactoryProps>) => React.ReactElement);

const getTableTestId = ({
  testid,
  queryKey,
  pillarTableContext,
}: Partial<PillarTableContextFactoryProps>): string => {
  if (pillarTableContext?.testidTable) {
    return pillarTableContext.testidTable;
  }
  if (testid) {
    return testid;
  }
  if (queryKey && typeof queryKey === "string") {
    return queryKey;
  }
  if (queryKey && Array.isArray(queryKey)) {
    return queryKey.join("-");
  }
  throw new Error(
    "You must provide a testid or queryKey to the PillarTable component.",
  );
};
